import { useTranslation as useReactTranslation } from "react-i18next";

import type { Dictionary } from "../__generated__/dictionary";

/** The `t` returned by {@link useTranslation}, for helpers that build copy outside a component. */
export type TranslateFn = (key: keyof Dictionary, params?: Record<string, unknown>) => string;

/**
 * Typed wrapper around `react-i18next`'s `useTranslation` hook.
 *
 * Returns a `t` function whose keys are constrained to the generated
 * `Dictionary` type, providing compile-time safety for translation keys.
 *
 * @example
 * ```tsx
 * const { t } = useTranslation();
 * return <span>{t("common.save")}</span>;
 * ```
 */
export function useTranslation(options?: UseTranslationOptions) {
  const { t, i18n } = useReactTranslation(undefined, options);

  return {
    /** Translate a known key from the dictionary. */
    t: t as TranslateFn,
    /** The underlying i18next instance (for advanced use only). */
    i18n,
  };
}

type UseTranslationOptions = {
  keyPrefix?: string;
};
